import React, { useState, useEffect } from 'react';
import Button from '../shared/Button';
import Modal from '../shared/Modal';
import ConfirmDialog from '../shared/ConfirmDialog';
import { useToast } from '../../hooks/useToast';
import { useConfirmDialog } from '../../hooks/useConfirmDialog';
import {
    getAllFactories,
    createFactory,
    updateFactory,
    deleteFactory
} from '../../services/firestore/factoryService';
import {
    getAllDoorGroups,
    createDoorGroup,
    updateDoorGroup,
    deleteDoorGroup
} from '../../services/firestore/doorGroupService';

const LEVELS = {
    FACTORIES: 'factories',
    DOOR_GROUPS: 'door_groups'
};

const MasterDataPanel = ({ user, initialActiveLevel }) => {
    const { success, error } = useToast();
    const { confirmConfig, showConfirm, closeConfirm } = useConfirmDialog();
    const [activeLevel, setActiveLevel] = useState(initialActiveLevel || LEVELS.FACTORIES);
    const [loading, setLoading] = useState(false);

    // Update active level if prop changes
    useEffect(() => {
        if (initialActiveLevel) {
            setActiveLevel(initialActiveLevel);
        }
    }, [initialActiveLevel]);

    // Data states
    const [factories, setFactories] = useState([]);
    const [doorGroups, setDoorGroups] = useState([]);

    // Modal states
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [formData, setFormData] = useState({});

    useEffect(() => {
        if (user?.tenantId) {
            loadAllData();
        }
    }, [user?.tenantId]);

    const loadAllData = async () => {
        setLoading(true);
        try {
            const [factoriesData, doorGroupsData] = await Promise.all([
                getAllFactories(user.tenantId),
                getAllDoorGroups(user.tenantId)
            ]);
            setFactories(factoriesData);
            setDoorGroups(doorGroupsData);
        } catch (err) {
            error('Veriler yüklenemedi');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (item = null) => {
        if (item) {
            setEditingItem(item);
            setFormData(item);
        } else {
            setEditingItem(null);
            setFormData({ tenantId: user.tenantId });
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingItem(null);
        setFormData({});
    };

    const handleSave = async () => {
        try {
            if (activeLevel === LEVELS.FACTORIES) {
                // Ensure factory is linked to tenant (which acts as company)
                const payload = { ...formData, tenantId: user.tenantId };

                if (editingItem) {
                    await updateFactory(editingItem.id, payload, user.tenantId);
                    success('Fabrika güncellendi');
                } else {
                    await createFactory(payload, user.tenantId);
                    success('Fabrika oluşturuldu');
                }
            } else if (activeLevel === LEVELS.DOOR_GROUPS) {
                if (editingItem) {
                    await updateDoorGroup(editingItem.id, formData, user.tenantId);
                    success('Rampa Grubu güncellendi');
                } else {
                    await createDoorGroup(formData, user.tenantId);
                    success('Rampa Grubu oluşturuldu');
                }
            }

            handleCloseModal();
            loadAllData();
        } catch (err) {
            error('İşlem başarısız: ' + err.message);
        }
    };

    const handleDelete = async (id) => {
        showConfirm({
            title: activeLevel === LEVELS.FACTORIES ? 'Fabrikayı Sil' : 'Rampa Grubunu Sil',
            message: `Bu ${activeLevel === LEVELS.FACTORIES ? 'fabrikayı' : 'rampa grubunu'} silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`,
            type: 'error',
            confirmText: 'Evet, Sil',
            onConfirm: async () => {
                try {
                    if (activeLevel === LEVELS.FACTORIES) {
                        await deleteFactory(id, user.tenantId);
                        success('Fabrika silindi');
                    } else if (activeLevel === LEVELS.DOOR_GROUPS) {
                        await deleteDoorGroup(id, user.tenantId);
                        success('Rampa Grubu silindi');
                    }

                    loadAllData();
                } catch (err) {
                    error('Silme başarısız: ' + err.message);
                }
            }
        });
    };

    const renderTable = () => {
        let data = [];
        let columns = [];

        if (activeLevel === LEVELS.FACTORIES) {
            data = factories;
            columns = ['Fabrika Adı', 'Adres', 'Durum', 'İşlemler'];
        } else if (activeLevel === LEVELS.DOOR_GROUPS) {
            data = doorGroups;
            columns = ['Rampa Grubu', 'Fabrika', 'Açıklama', 'Durum', 'İşlemler'];
        }

        if (loading) {
            return (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                    <div className="badge badge-pending">Yükleniyor...</div>
                </div>
            );
        }

        if (data.length === 0) {
            return (
                <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
                    <div style={{ fontSize: '2rem', marginBottom: '12px' }}>📦</div>
                    Henüz kayıt bulunmuyor.
                </div>
            );
        }

        return (
            <table className="table">
                <thead>
                    <tr>
                        {columns.map(col => <th key={col}>{col}</th>)}
                    </tr>
                </thead>
                <tbody>
                    {data.map(item => (
                        <tr key={item.id}>
                            {activeLevel === LEVELS.FACTORIES && (
                                <>
                                    <td style={{ fontWeight: 'var(--font-bold)' }}>{item.name}</td>
                                    <td>{item.address || '-'}</td>
                                    <td>
                                        <span className={`badge ${item.isActive ? 'badge-completed' : 'badge-pending'}`}>
                                            {item.isActive ? 'Aktif' : 'Pasif'}
                                        </span>
                                    </td>
                                </>
                            )}
                            {activeLevel === LEVELS.DOOR_GROUPS && (
                                <>
                                    <td style={{ fontWeight: 'var(--font-bold)' }}>{item.name}</td>
                                    <td>{factories.find(f => f.id === item.factoryId)?.name || '-'}</td>
                                    <td>{item.description || '-'}</td>
                                    <td>
                                        <span className={`badge ${item.isActive ? 'badge-completed' : 'badge-pending'}`}>
                                            {item.isActive ? 'Aktif' : 'Pasif'}
                                        </span>
                                    </td>
                                </>
                            )}
                            <td>
                                <div className="flex gap-sm">
                                    <Button size="sm" variant="secondary" onClick={() => handleOpenModal(item)}>
                                        Düzenle
                                    </Button>
                                    <Button size="sm" variant="danger" onClick={() => handleDelete(item.id)}>
                                        Sil
                                    </Button>
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    };

    const renderModalContent = () => {
        if (activeLevel === LEVELS.FACTORIES) {
            return (
                <>
                    <div className="form-group">
                        <label className="form-label">Fabrika Adı *</label>
                        <input
                            type="text"
                            className="input"
                            required
                            placeholder="Örn: Tuzla Depo"
                            value={formData.name || ''}
                            onChange={e => setFormData({ ...formData, name: e.target.value })}
                        />
                    </div>
                    <div className="form-group">
                        <label className="form-label">Adres</label>
                        <textarea
                            className="textarea"
                            placeholder="Açık adres..."
                            value={formData.address || ''}
                            onChange={e => setFormData({ ...formData, address: e.target.value })}
                        />
                    </div>
                </>
            );
        } else if (activeLevel === LEVELS.DOOR_GROUPS) {
            return (
                <>
                    <div className="form-group">
                        <label className="form-label">Rampa Grubu Adı *</label>
                        <input
                            type="text"
                            className="input"
                            required
                            placeholder="Örn: Batı Rampaları"
                            value={formData.name || ''}
                            onChange={e => setFormData({ ...formData, name: e.target.value })}
                        />
                    </div>
                    <div className="form-group">
                        <label className="form-label">Bağlı Olduğu Fabrika *</label>
                        <select
                            className="select"
                            required
                            value={formData.factoryId || ''}
                            onChange={e => setFormData({ ...formData, factoryId: e.target.value })}
                        >
                            <option value="">Seçiniz...</option>
                            {factories.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                        </select>
                    </div>
                    <div className="form-group">
                        <label className="form-label">Açıklama</label>
                        <textarea
                            className="textarea"
                            placeholder="Örn: 1-10 arası rampalar"
                            value={formData.description || ''}
                            onChange={e => setFormData({ ...formData, description: e.target.value })}
                        />
                    </div>
                </>
            );
        }
    };

    const levels = [
        { key: LEVELS.FACTORIES, label: 'Fabrikalar', icon: '🏭', count: factories.length },
        { key: LEVELS.DOOR_GROUPS, label: 'Rampa Grupları', icon: '🚪', count: doorGroups.length }
    ];

    return (
        <div>
            {/* Level Selector */}
            <div className="flex gap-md mb-lg">
                {levels.map(level => (
                    <button
                        key={level.key}
                        onClick={() => setActiveLevel(level.key)}
                        className={`card ${activeLevel === level.key ? 'led-border' : ''}`}
                        style={{
                            flex: 1,
                            padding: 'var(--spacing-lg)',
                            border: activeLevel === level.key ? '2px solid var(--primary-color)' : '1px solid var(--border-color)',
                            background: activeLevel === level.key ? 'var(--bg-card)' : 'var(--bg-hover)',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            textAlign: 'center'
                        }}
                    >
                        <div style={{ fontSize: '2rem', marginBottom: '8px' }}>{level.icon}</div>
                        <div style={{ fontWeight: 'var(--font-bold)', color: 'var(--text-primary)' }}>{level.label}</div>
                        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>{level.count} kayıt</div>
                    </button>
                ))}
            </div>

            {/* Action Button */}
            <div className="flex justify-between items-center mb-lg">
                <h3>{levels.find(l => l.key === activeLevel)?.label}</h3>
                <Button variant="primary" onClick={() => handleOpenModal()}>
                    + Yeni Ekle
                </Button>
            </div>

            {/* Table */}
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                {renderTable()}
            </div>

            {/* Modal */}
            <Modal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                title={editingItem ? 'Düzenle' : 'Yeni Ekle'}
            >
                {renderModalContent()}
                <div className="flex gap-md justify-end mt-lg">
                    <Button variant="secondary" onClick={handleCloseModal}>İptal</Button>
                    <Button variant="primary" onClick={handleSave}>Kaydet</Button>
                </div>
            </Modal>

            <ConfirmDialog
                isOpen={confirmConfig.isOpen}
                onClose={closeConfirm}
                onConfirm={confirmConfig.onConfirm}
                title={confirmConfig.title}
                message={confirmConfig.message}
                type={confirmConfig.type}
                confirmText={confirmConfig.confirmText}
                cancelText={confirmConfig.cancelText}
            />
        </div>
    );
};

export default MasterDataPanel;
